import { useCallback } from 'react'
import { DirectUpload } from '@rails/activestorage'
import { builderCsrfToken } from './api'

export const UNREACHABLE_STORAGE_MESSAGE =
  'we could not reach the file storage service. A network, firewall, or browser extension may be ' +
  'blocking it. Check your connection and try again.'
export const STORAGE_IDLE_TIMEOUT_MS = 20000

// ActiveStorage reports failures as strings, so read the completed XHR for detail.
function serverErrorFrom(xhr) {
  if (!xhr || xhr.status < 400) return null

  try {
    const response = xhr.responseType === 'json' ? xhr.response : JSON.parse(xhr.responseText)
    return response?.error || null
  } catch {
    return null
  }
}

function failureMessage({ error, serverMessage, storeStatus }) {
  if (serverMessage) return serverMessage
  // Status 0 means the browser could not access the storage response.
  if (storeStatus === 0) return UNREACHABLE_STORAGE_MESSAGE
  if (storeStatus >= 400) {
    return `the file storage service rejected this upload (status ${storeStatus}). Please try again.`
  }
  if (typeof error === 'string') return error
  return error?.message || 'upload failed'
}

export function useDirectUpload(vendorToken, builderSessionId, builderDraftPublicId) {
  const uploadFile = useCallback((file, { onProgress } = {}) => {
    return new Promise((resolve, reject) => {
      const url = `/builder/${vendorToken}/direct_uploads`
      let blobXhr = null
      let storeXhr = null
      let idleTimer
      let settled = false
      const delegate = {
        directUploadWillCreateBlobWithXHR(xhr) {
          blobXhr = xhr
          xhr.setRequestHeader('X-Builder-Session', builderSessionId)
          if (builderDraftPublicId) xhr.setRequestHeader('X-Builder-Draft', builderDraftPublicId)
          xhr.setRequestHeader(
            'X-CSRF-Token',
            document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || ''
          )
          // ActiveStorage builds this XHR itself, so it never goes through
          // buildHeaders. Without this the presign is refused as a forgery.
          xhr.setRequestHeader('X-Builder-CSRF', builderCsrfToken())
        },
        directUploadWillStoreFileWithXHR(xhr) {
          storeXhr = xhr
          const resetIdleTimer = () => {
            if (settled) return
            clearTimeout(idleTimer)
            idleTimer = setTimeout(() => {
              if (settled) return
              settled = true
              const storageStatus = xhr.status
              xhr.abort()
              reject(Object.assign(new Error(failureMessage({
                error: 'The upload timed out. Please try again.', storeStatus: storageStatus,
              })), {
                uploadPhase: 'storage', storageStatus,
              }))
            }, STORAGE_IDLE_TIMEOUT_MS)
          }
          resetIdleTimer()
          xhr.upload.addEventListener('progress', (event) => {
            resetIdleTimer()
            if (onProgress) {
              if (event.lengthComputable) {
                const percent = Math.round((event.loaded / event.total) * 100)
                onProgress(percent)
              }
            }
          })
        }
      }
      const upload = new DirectUpload(file, url, delegate)
      upload.create((error, blob) => {
        if (settled) return
        settled = true
        clearTimeout(idleTimer)
        if (error) {
          // ActiveStorage's callback precedes our load/loadend listeners.
          const failure = new Error(failureMessage({
            error,
            serverMessage: serverErrorFrom(blobXhr),
            storeStatus: storeXhr?.status ?? null,
          }))
          failure.uploadPhase = storeXhr ? 'storage' : 'presign'
          failure.storageStatus = storeXhr?.status ?? null
          reject(failure)
        } else resolve(blob)
      })
    })
  }, [builderDraftPublicId, builderSessionId, vendorToken])

  return { uploadFile }
}
